import { POST_CALL_CRM_WRITEBACK_LABELS, QUALIFICATION_FIELD_LABELS, type PostCall, type QualificationField } from '@lia/contracts';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { StatusBadge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { RadioGroup } from '../primitives/checkbox.js';
import { Textarea } from '../primitives/input.js';
import { InlineNotice } from '../primitives/notice.js';
import { SimulationBadge } from './copilot-live.js';
import { POST_CALL_CRM_WRITEBACK_TONE, POST_CALL_STATUS_LABELS, POST_CALL_STATUS_TONE } from './copilot-status.js';

// Post-call review (addendum §11): a draft the advisor confirms or rejects item by item. Nothing is written unless the
// advisor confirms it; unresolved questions and the follow-up are recommendations only (never sent by the copilot).

export type ItemDecision = 'pending' | 'accepted' | 'rejected';
export type SummaryDecision = 'pending' | 'confirmed' | 'rejected';

export interface PostCallReviewInput {
  accept: QualificationField[];
  reject: QualificationField[];
  summary?: string;
}

/** Only explicitly decided items go to the server; the summary only when confirmed. */
export function postCallReviewPayload(decisions: Partial<Record<QualificationField, ItemDecision>>, summaryDecision: SummaryDecision, summaryText: string): PostCallReviewInput {
  const entries = Object.entries(decisions) as [QualificationField, ItemDecision][];
  const out: PostCallReviewInput = { accept: entries.filter(([, d]) => d === 'accepted').map(([f]) => f), reject: entries.filter(([, d]) => d === 'rejected').map(([f]) => f) };
  const text = summaryText.trim();
  if (summaryDecision === 'confirmed' && text) out.summary = text;
  return out;
}

const DECISION_OPTIONS = [
  { value: 'accepted', label: 'Confirmar' },
  { value: 'rejected', label: 'Rechazar' },
  { value: 'pending', label: 'Decidir después' },
];
const DECISION_LABEL: Record<ItemDecision, string> = { pending: 'Sin decidir', accepted: 'Confirmado', rejected: 'Rechazado' };
const DECISION_TONE = { pending: 'neutral', accepted: 'success', rejected: 'danger' } as const;

export interface PostCallReviewProps {
  data: PostCall;
  timezone: string;
  onSubmit: (input: PostCallReviewInput) => void;
  submitting?: boolean;
  /** False for viewers who may read but not review (e.g. a manager watching another advisor's call). */
  canReview?: boolean;
  error?: string | null;
  className?: string;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2">
      <h3 className="text-body font-semibold text-text-primary">{title}</h3>
      {children}
    </section>
  );
}

export function PostCallReview({ data, timezone, onSubmit, submitting, canReview = true, error, className }: PostCallReviewProps) {
  const reviewable = data.status === 'ready' && canReview;
  const [decisions, setDecisions] = React.useState<Partial<Record<QualificationField, ItemDecision>>>({});
  const [summaryDecision, setSummaryDecision] = React.useState<SummaryDecision>('pending');
  const [summary, setSummary] = React.useState(data.summary ?? '');
  React.useEffect(() => setSummary(data.summary ?? ''), [data.summary]);
  const payload = postCallReviewPayload(decisions, summaryDecision, summary);
  const hasDecision = payload.accept.length + payload.reject.length > 0 || payload.summary !== undefined;

  const header = (
    <div className="flex flex-wrap items-center gap-2">
      <StatusBadge tone={POST_CALL_STATUS_TONE[data.status]}>{POST_CALL_STATUS_LABELS[data.status]}</StatusBadge>
      {data.simulated ? <SimulationBadge /> : null}
      <span className="text-meta text-text-secondary">
        Cobertura del playbook: {data.coverage.covered} de {data.coverage.required} obligatorios
        {data.coverage.notApplicable ? ` (${data.coverage.notApplicable} no aplican)` : ''}
      </span>
    </div>
  );

  if (data.status === 'none' || data.status === 'pending' || data.status === 'unavailable') {
    return (
      <div className={cn('grid gap-3', className)}>
        {header}
        <p className="text-body text-text-secondary">
          {data.status === 'pending'
            ? 'El borrador se está preparando. Aparecerá aquí cuando esté listo.'
            : data.status === 'unavailable'
              ? (data.reason ?? 'No hay borrador para esta llamada.')
              : 'Esta sesión no tiene borrador posterior a la llamada.'}
        </p>
      </div>
    );
  }

  return (
    <form
      className={cn('grid gap-4', className)}
      onSubmit={(e) => {
        e.preventDefault();
        if (reviewable && hasDecision) onSubmit(payload);
      }}
    >
      {header}
      {reviewable ? (
        <InlineNotice tone="info">Revisa cada dato. Solo se guarda lo que confirmes; nada se envía al prospecto ni se agenda desde aquí.</InlineNotice>
      ) : data.status === 'reviewed' ? (
        <p className="text-meta text-text-secondary">
          Revisada{data.reviewedBy ? ` por ${data.reviewedBy}` : ''}
          {data.reviewedAt ? ` el ${formatDateTime(data.reviewedAt, timezone)}` : ''}.
        </p>
      ) : null}

      <Section title="Resumen">
        {reviewable ? (
          <fieldset className="grid gap-2 rounded-control border border-border-subtle p-3">
            <legend className="px-1 text-meta font-medium text-text-secondary">Resumen del borrador</legend>
            <Textarea aria-label="Texto del resumen" value={summary} rows={4} maxLength={8000} onChange={(e) => setSummary(e.target.value)} />
            <p className="text-meta text-text-secondary">Puedes editarlo. Se guarda (y se envía al CRM, si aplica) solo si lo confirmas.</p>
            <RadioGroup aria-label="Decisión sobre el resumen" orientation="horizontal" value={summaryDecision === 'confirmed' ? 'accepted' : summaryDecision} onValueChange={(v) => setSummaryDecision(v === 'accepted' ? 'confirmed' : (v as SummaryDecision))} options={DECISION_OPTIONS} />
          </fieldset>
        ) : (
          <p className="whitespace-pre-line text-body text-text-primary">{data.summary ?? 'Sin resumen.'}</p>
        )}
      </Section>

      <Section title="Datos de calificación sugeridos">
        {data.qualification.length ? (
          <ul className="grid gap-2">
            {data.qualification.map((q) => {
              const label = QUALIFICATION_FIELD_LABELS[q.field];
              const current = decisions[q.field] ?? q.decision;
              return (
                <li key={q.field}>
                  <fieldset className="grid gap-1.5 rounded-control border border-border-subtle p-3">
                    <legend className="px-1 text-meta font-medium text-text-secondary">{label}</legend>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex-1 text-body font-medium text-text-primary">{q.value}</span>
                      <StatusBadge tone={DECISION_TONE[current]}>{DECISION_LABEL[current]}</StatusBadge>
                    </div>
                    {q.evidence.map((e) => (
                      <blockquote key={e.utteranceId} className="border-l-2 border-border-control pl-2 text-meta text-text-secondary">
                        <span className="sr-only">Evidencia: </span>«{e.excerpt}»
                      </blockquote>
                    ))}
                    {!q.evidence.length ? <p className="text-meta text-text-secondary">Sin frase de la transcripción que lo respalde.</p> : null}
                    {reviewable && q.decision === 'pending' ? (
                      <RadioGroup
                        aria-label={`Decisión sobre ${label}`}
                        orientation="horizontal"
                        value={current}
                        onValueChange={(v) => setDecisions((d) => ({ ...d, [q.field]: v as ItemDecision }))}
                        options={DECISION_OPTIONS}
                      />
                    ) : null}
                  </fieldset>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-body text-text-secondary">No hay datos de calificación sugeridos.</p>
        )}
      </Section>

      <Section title="Preguntas sin resolver">
        {data.unresolvedQuestions.length ? (
          <ul className="grid list-disc gap-1 pl-5 text-body text-text-primary">
            {data.unresolvedQuestions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        ) : (
          <p className="text-body text-text-secondary">Ninguna.</p>
        )}
        <p className="text-meta text-text-secondary">Solo como referencia: no se guardan ni se envían.</p>
      </Section>

      <Section title="Seguimiento recomendado">
        <p className="text-body text-text-primary">{data.followUp ?? 'Sin recomendación.'}</p>
        <p className="text-meta text-text-secondary">Recomendación: el copiloto no envía mensajes ni agenda citas.</p>
      </Section>

      {data.appliedWrites.length ? (
        <Section title="Guardado tras la revisión">
          <ul className="grid list-disc gap-1 pl-5 text-body text-text-primary">
            {data.appliedWrites.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {data.status === 'reviewed' || data.crmWriteback.status !== 'none' ? (
        <section aria-label="CRM" className="grid gap-2">
          <h3 className="text-body font-semibold text-text-primary">CRM</h3>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={POST_CALL_CRM_WRITEBACK_TONE[data.crmWriteback.status]}>{POST_CALL_CRM_WRITEBACK_LABELS[data.crmWriteback.status]}</StatusBadge>
            {data.crmWriteback.updatedAt ? <span className="text-meta text-text-secondary">{formatDateTime(data.crmWriteback.updatedAt, timezone)}</span> : null}
          </div>
          {data.crmWriteback.reason ? <p className="text-body text-text-primary">{data.crmWriteback.reason}</p> : null}
          <p className="text-meta text-text-secondary">
            Solo se envía lo que el asesor confirmó, con la política de escritura de la conexión del CRM. Escritura al CRM sin verificar con una cuenta real.
          </p>
        </section>
      ) : null}

      {error ? (
        <InlineNotice tone="danger" role="alert">
          {error}
        </InlineNotice>
      ) : null}
      {reviewable ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" loading={submitting} disabled={!hasDecision}>
            Guardar revisión
          </Button>
          {!hasDecision ? <span className="text-meta text-text-secondary">Confirma o rechaza al menos un dato.</span> : null}
        </div>
      ) : null}
    </form>
  );
}
